import { SupabaseSelectResult } from "@/lib/supabase/supabase-postgrest";
import { Dinero, dineroForCurrencyFromFloatingPoint } from "@/utils/dinero-util";
import { CostTaxBreakdown } from "./cost-tax-breakdown";
import { CADCurrencyConverter } from "./currency";
import { itemUOMSelect, ItemUOMSelectRelatedTables, ItemUOMSelectResult } from "./item-uoms";
import {
  getCurrencyCodeForVendor,
  vendorPricingInformationSelect,
  VendorSelectRelatedTables,
  VendorWithPricingInformation,
} from "./vendor";

export interface Item extends ItemSelectResult {
  baseItemUOM?: ItemUOMSelectResult;
  preferredPurchaseCost: CostTaxBreakdown;
  leadTimeMs: number | null;
}

export type ItemSelectResult = SupabaseSelectResult<"Item", typeof itemSelect, ItemSelectRelatedTables>;

export type ItemSelectRelatedTables =
  | "Vendor"
  | "VendorItemCatalog"
  | "ItemUOM"
  | ItemUOMSelectRelatedTables
  | VendorSelectRelatedTables;

export const itemSelect = `
  id,
  name,
  type,
  description,
  purchaseCost,
  leadTimeDays:leadTime,
  preferredVendor:Vendor!vendorId(${vendorPricingInformationSelect}),
  purchaseTaxCode:TaxCode!purchaseTaxCodeId(*),
  catalogEntries:VendorItemCatalog(
    price,
    vendorPartNumber,
    vendor:Vendor!vendorId(${vendorPricingInformationSelect})
  ),
  uoms:ItemUOM(${itemUOMSelect})
`;

export function processItem(item: ItemSelectResult, CADCurrencyConverter: CADCurrencyConverter): Item {
  return {
    ...item,
    preferredPurchaseCost: getPurchaseCostForItemAndVendor(item, CADCurrencyConverter),
    baseItemUOM: item.uoms.find((x) => x.isBase),
    leadTimeMs: item.leadTimeDays ? item.leadTimeDays * 24 * 60 * 60 * 1000 : null,
  };
}

/**
 * Get the purchase cost for a specific vendor and item combination.
 *
 * See {@link getPreferredPurchaseSubtotalCad} for details on how the cost is calculated.
 *
 * The tax code from the vendor is used. If there is no tax code on the vendor, it is an error and we don't fall back
 * to the tax code on the item.
 */
function getPurchaseCostForItemAndVendor(
  item: ItemSelectResult,
  CADCurrencyConverter: CADCurrencyConverter,
): CostTaxBreakdown {
  // Get the subtotal for the item, return an error on failure
  const subtotalResult = getPreferredPurchaseSubtotalCad(item, CADCurrencyConverter);
  if ("error" in subtotalResult) return CostTaxBreakdown.withError(subtotalResult.error);

  // Compute the full cost breakdown - use the tax code from the item, falling back to the vendor's tax code
  return CostTaxBreakdown.forItemAndVendor({ item, ...subtotalResult });
}

/**
 * Get the purchase cost subtotal for the specified item, and the vendor associated with the purchase subtotal.
 *
 * If the item has a preferred vendor, the cost comes from the associated vendor-item catalog entry, if any, falling
 * back to the item purchase cost.
 *
 * If the item has no preferred vendor, but a single vendor-item catalog entry exists, the cost comes from that entry.
 *
 * Otherwise, an error message is returned.
 */
function getPreferredPurchaseSubtotalCad(
  item: ItemSelectResult,
  CADCurrencyConverter: CADCurrencyConverter,
): { subtotal: Dinero; vendor: VendorWithPricingInformation } | { error: string } {
  const vendor = item.preferredVendor;
  if (vendor) {
    // If the item has a preferred vendor, use the catalog entry for that vendor if it exists, fall back to the item's purchase cost
    const catalogEntry = item.catalogEntries.find((x) => x.vendor.id === vendor.id);
    const vendorCurrencyCode = getCurrencyCodeForVendor(vendor);
    const purchaseCost = dineroForCurrencyFromFloatingPoint(
      catalogEntry ? catalogEntry.price : item.purchaseCost,
      vendorCurrencyCode,
    );
    return { subtotal: CADCurrencyConverter(purchaseCost), vendor };
  } else if (item.catalogEntries.length > 1) {
    // Return an error if there were multiple catalog entries to choose from
    return { error: `Item '${item.name}' has multiple vendor-item catalog entries and no preferred vendor` };
  } else if (item.catalogEntries.length) {
    // If the item has no preferred vendor, but a single catalog entry, use that entry
    const { price, vendor } = item.catalogEntries[0]!;
    const vendorCurrencyCode = getCurrencyCodeForVendor(vendor);
    const purchaseCost = dineroForCurrencyFromFloatingPoint(price, vendorCurrencyCode);
    return { subtotal: CADCurrencyConverter(purchaseCost), vendor };
  } else {
    // Otherwise there are no catalog entries or preferred vendor, return an error
    return { error: `Item '${item.name}' has no preferred vendor or vendor-item catalog entries` };
  }
}
